import PageHeader from "@/components/PageHeader";
import { CtaBanner } from "@/components/CTA";
import { Section } from "@/pages/Home";
import Seo from "@/components/Seo";
import { IMG, ImageBreak } from "@/components/ImageAssets";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldCheck, HeartHandshake, Handshake, Compass, UserCircle2,
  Lock, Users, LineChart, Sparkles, Linkedin, ArrowRight,
} from "lucide-react";

const values = [
  { icon: <ShieldCheck className="w-5 h-5" />, name: "Accountability", body: "Every referral has a status. Every partner has a role. Nothing falls between the cracks." },
  { icon: <HeartHandshake className="w-5 h-5" />, name: "Human Connection", body: "Technology serves the person — never replaces the relationship at the center of care." },
  { icon: <Handshake className="w-5 h-5" />, name: "Trust", body: "Consent-first, verified partners, and secure handoffs. Trust is earned in every interaction." },
  { icon: <Compass className="w-5 h-5" />, name: "Service", body: "We measure ourselves by how well the systems we build serve the people who use them." },
  { icon: <UserCircle2 className="w-5 h-5" />, name: "Dignity", body: "Every veteran, family member, and community member deserves to be seen, heard, and helped." },
  { icon: <Lock className="w-5 h-5" />, name: "Privacy", body: "Role-based access, consent-based sharing, and information that stays where it belongs." },
  { icon: <Users className="w-5 h-5" />, name: "Partnership", body: "We build with — not for. Partner organizations shape how WHO works from day one." },
  { icon: <LineChart className="w-5 h-5" />, name: "Measurable Impact", body: "Activity is not outcome. WHO exists to help partners measure what actually happens." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About WHO | Coordination Infrastructure for Human Services"
        description="WHO stands for Warm Hand Off. We are a mission-driven coordination infrastructure company. WHO Guides™ — real people supported by nine specialist AI agents — coordinate warm handoffs between veterans, families, and the organizations that serve them."
        path="/about"
      />

      <PageHeader
        eyebrow="About WHO · A coordination infrastructure company"
        title="Built around the belief that no one should navigate fragmented systems alone."
        lede="WHO stands for Warm Hand Off. We are a mission-driven infrastructure company. WHO Guides™ &mdash; real people supported by nine specialist AI agents &mdash; coordinate warm handoffs between veterans, families, and the organizations that serve them."
      />

      {/* About Hero Graphic — reinforces the H1 visually */}
      <section className="py-16 lg:py-20 bg-cream border-b border-borderSubtle" data-testid="about-hero-graphic-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="rounded-lg overflow-hidden shadow-2xl border border-gold/20">
            <img
              src="/assets/about-hero.png"
              alt="A military service member walking beside his family — a mother and two young children — held in the palm of an outstretched hand at sunset. Surrounding them are seven glowing icons of the WHO support ecosystem: Community, Health & Wellness, Employment, Education, Housing, Family Support, and VA Benefits — each connected by a network of light. Alongside: 'Built around the belief that no one should navigate fragmented systems alone.' WHO — Warm Hand Off. We connect. We confirm. We follow through. Because every connection can change a life."
              className="w-full h-auto block"
              loading="lazy"
              data-testid="about-hero-image"
            />
          </div>
        </div>
      </section>

      {/* About / Origin */}
      <Section eyebrow="About WHO" title="A response to a real-world problem.">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-5 text-lg text-slateSoft leading-relaxed">
            <p>
              WHO was created because people are often referred to services without anyone
              confirming whether they were actually connected, helped, or supported through the
              next step. A phone number, a brochure, a website — none of those is a real connection.
            </p>
            <p>
              We built WHO to close that gap &mdash; not with another program, and not with another
              app, but with a coordination platform that lets a person speak a real need out loud
              and be warm-handed off to a verified partner. <span className="text-navy font-semibold">WHO
              Guides&trade;</span> &mdash; real people supported by nine specialist AI agents &mdash;
              own the connection, coordinate with partners, and stay with the person until the
              handoff is real.
            </p>
            <p className="text-navy font-medium">
              We are veterans, public-sector leaders, technologists, and community members who
              believe systems can — and must — work better for the people they are meant to serve.
            </p>
          </div>
          <aside className="lg:col-span-4 p-7 bg-cream-panel border-l-4 border-gold rounded-md" data-testid="about-callout">
            <p className="eyebrow text-gold-deep">What WHO stands for</p>
            <p className="mt-4 font-display font-bold text-navy text-xl leading-snug">
              <span className="text-gold">W</span>arm <span className="text-gold">H</span>and <span className="text-gold">O</span>ff
            </p>
            <p className="mt-4 text-slateSoft leading-relaxed">
              A warm handoff means direct connection, confirmation, follow-up, and shared
              responsibility between the person in need and the provider able to help.
            </p>
          </aside>
        </div>
      </Section>

      {/* Meet Our Founder — summary + official portrait + LinkedIn */}
      <Section eyebrow="Meet Our Founder" title="Led by someone who has lived the handoff." bg="bone" id="meet-our-founder">
        <div className="grid lg:grid-cols-12 gap-10 items-center" data-testid="about-founder-section">
          <div className="lg:col-span-4">
            <div className="rounded-lg overflow-hidden border border-borderSubtle shadow-lg bg-cream-panel max-w-[340px]">
              <img
                src="/assets/founder-marlon-ceo-square.webp"
                width="480"
                height="480"
                alt="Marlon L. Ferguson, Founder and Chief Executive Officer of Warm Hand Off LLC."
                className="w-full h-auto block"
                loading="lazy"
                data-testid="about-founder-image"
              />
            </div>
          </div>
          <div className="lg:col-span-8">
            <p className="eyebrow text-gold-deep">Marlon L. Ferguson</p>
            <h3 className="mt-2 font-display font-black text-navy text-2xl lg:text-3xl leading-tight">
              Founder &amp; Chief Executive Officer, Warm Hand Off LLC
            </h3>
            <p className="mt-2 text-sm uppercase tracking-[0.18em] text-navy/60 font-semibold">
              Servant Leader | Inquisitive Problem-Solver | Lifelong Learner
            </p>
            <p className="mt-5 text-lg text-slateSoft leading-relaxed max-w-2xl">
              A U.S. Navy veteran and leader across government, higher education, and nonprofit
              service, Marlon founded WHO to close the gap between a referral made and a connection
              achieved &mdash; combining genuine human connection with responsible AI and measurable
              accountability.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="https://www.linkedin.com/in/marlonlferguson/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-cream font-semibold px-4 py-2.5 rounded-md text-sm transition-colors"
                data-testid="about-founder-linkedin"
              >
                <Linkedin className="w-4 h-4" /> Connect with Marlon on LinkedIn
              </a>
              <Link
                to="/founder"
                className="inline-flex items-center gap-2 border border-navy/25 hover:border-navy text-navy font-semibold px-4 py-2.5 rounded-md text-sm transition-colors"
                data-testid="about-founder-fullbio"
              >
                Read Marlon&rsquo;s full story <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* Why the Economics of Connection Matters */}
      <section className="py-16 lg:py-20 bg-white border-b border-borderSubtle" data-testid="about-economics-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <p className="eyebrow text-gold-deep">The Economics of Connection</p>
            <h2 className="mt-3 font-display font-black text-navy tracking-tight leading-[1.05] text-2xl lg:text-3xl">
              Why the Economics of Connection Matters
            </h2>
            <p className="mt-5 text-slateSoft leading-relaxed max-w-2xl">
              WHO seeks to address both sides of the problem at once: the individual experience of navigating services — one veteran, one family at a time — and the broader challenge of fragmented coordination across the systems meant to serve them. Understanding whether connections actually happen, and what they cost, is how communities learn to serve veterans better.
            </p>
            <Link to="/impact" data-testid="about-economics-link" className="mt-6 inline-flex items-center gap-2 text-navy font-semibold underline decoration-gold underline-offset-4 hover:text-gold-deep transition-colors">
              See how we think about impact <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Our Mission — branded infographic */}
      <section className="py-16 lg:py-20 bg-cream-panel border-y border-borderSubtle" data-testid="mission-infographic-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="rounded-lg overflow-hidden shadow-2xl border border-gold/25 bg-cream">
            <img
              src="/assets/our-mission.png"
              alt="Our Mission — WHO connects people to the support they need through accountable, human-centered warm handoffs that create paths to stability, opportunity, and well-being. Four-step arc: Connect the right person · Coordinate across organizations · Confirm the connection · Change lives (stronger individuals, stronger communities). One connection. One person. One community at a time."
              className="w-full h-auto block"
              loading="lazy"
              data-testid="our-mission-image"
            />
          </div>
        </div>
      </section>

      {/* Mission + Vision (side by side) */}
      <Section eyebrow="Mission & Vision" title="Why we exist. Where we&#39;re going." bg="cream">
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="p-10 bg-white border border-borderSubtle rounded-lg"
            data-testid="mission-card"
          >
            <p className="eyebrow text-gold-deep">Our Mission</p>
            <h3 className="mt-4 font-display font-black text-navy text-2xl lg:text-3xl leading-[1.15]">
              To transform disconnected referrals into accountable, measurable, human-centered connections.
            </h3>
            <p className="mt-5 text-slateSoft leading-relaxed">
              WHO exists to help veterans, families, and communities access the right support at
              the right time — with confirmation that the connection actually happened.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.5 }}
            className="p-10 bg-navy text-cream rounded-lg relative overflow-hidden"
            data-testid="vision-card"
          >
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gold/10" aria-hidden />
            <div className="relative">
              <p className="eyebrow text-gold-light">Our Vision</p>
              <h3 className="mt-4 font-display font-black text-cream text-2xl lg:text-3xl leading-[1.15]">
                A future where every referral becomes a real connection.
              </h3>
              <p className="mt-5 text-cream/85 leading-relaxed">
                Every organization coordinates with confidence. Every veteran or community member
                knows WHO can help — and how to reach them.
              </p>
            </div>
          </motion.div>
        </div>
      </Section>

      <ImageBreak
        src={IMG.communityMeeting}
        alt="A diverse community meeting around a table"
        eyebrow="Built with, not for"
        caption="Every partner has a seat. Every voice shapes what WHO becomes."
        testId="about-image-break"
        height="h-[320px] md:h-[400px]"
      />

      {/* Values */}
      <Section eyebrow="Our Values" title="Eight commitments that shape every decision we make.">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {values.map((v, i) => (
            <motion.div
              key={v.name}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              className="p-6 bg-white border border-borderSubtle rounded-lg hover:border-gold hover:shadow-md transition-all"
              data-testid={`value-${v.name.toLowerCase().replace(/\W+/g, "-")}`}
            >
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-md bg-navy text-gold">
                {v.icon}
              </span>
              <h3 className="mt-4 font-display font-bold text-navy text-lg">{v.name}</h3>
              <p className="mt-2 text-slateSoft leading-relaxed text-[15px]">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Why Warm Handoffs Matter */}
      <Section eyebrow="Why Warm Handoffs Matter" title="A handoff isn&#39;t a phone number. It&#39;s a promise." bg="cream">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="space-y-5 text-lg text-slateSoft leading-relaxed">
            <p>
              Warm handoffs are more than referrals. They involve <span className="text-navy font-semibold">direct
              connection, confirmation, follow-up, and responsibility</span> between the person in need and the
              provider able to help.
            </p>
            <p>
              For a veteran facing multiple transitions at once — housing, healthcare, employment,
              benefits, family — the difference between a warm handoff and a cold referral can be
              the difference between a resolved need and a crisis.
            </p>
            <p>
              Warm handoffs also change what organizations can see and improve. When referrals are
              tracked to outcome, systems learn where they succeed, where they stall, and where
              the community&apos;s next gap is emerging.
            </p>
          </div>
          <div className="p-8 lg:p-10 bg-white border border-borderSubtle rounded-lg" data-testid="more-than-app-card">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-gold" />
              <p className="eyebrow text-gold-deep">Coordination infrastructure &middot; Not just an app</p>
            </div>
            <p className="mt-4 font-display font-bold text-navy text-2xl leading-tight">
              WHO is not a directory. Not a referral tool. Not another app.
            </p>
            <p className="mt-4 text-slateSoft leading-relaxed">
              WHO is <span className="text-navy font-semibold">coordination infrastructure</span> for
              veterans, families, and the organizations that serve them. WHO Guides&trade; &mdash;
              real people supported by nine specialist AI agents, working with verified partners
              &mdash; own the moment help is needed, coordinate services, and stay in the loop
              until the connection is confirmed. It is the accountability and follow-through that a
              referral alone can never provide.
            </p>
          </div>
        </div>
      </Section>

      {/* Closing message */}
      <Section>
        <div className="max-w-4xl mx-auto text-center" data-testid="about-closing">
          <p className="eyebrow text-gold">In closing</p>
          <p className="mt-6 font-display font-black text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
            If not you, then <span className="text-gold">WHO</span>.
          </p>
          <p className="mt-6 text-slateSoft text-lg max-w-2xl mx-auto">
            The help exists. The system doesn&apos;t have to be the barrier. Join us in making the
            handoff warm, accountable, and human.
          </p>
        </div>
      </Section>

      <CtaBanner
        eyebrow="Join the mission."
        title="Help build the coordination infrastructure your community deserves."
        primaryText="Become a Partner"
        primaryTo="/partners"
        secondaryText="Meet WHO Guides"
        secondaryTo="/platform"
      />
    </div>
  );
}
